import { useRef, type Dispatch, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import {
  activateUserItemUserInventoryUserItemIdActivatePost,
  claimQuestRewardUserQuestsClaimRewardPost,
} from '../../../client';
import type { DailyQuestView } from '../../../models/quest';
import type { UserData } from '../../../models/user';
import { showAlert } from '../../../utils/telegram/alert';
import { impactOccurred } from '../../../utils/telegram/hapticFeedback';
import { applyUserProgressDelta } from '../../../utils/userProgress';
import { toApiError } from '../../../utils/net/apiError';
import posthog from 'posthog-js';
import { globalClaimedQuestTimestamps } from '../constants';
import { markRewardClaimedLocally, unmarkRewardClaimedLocally } from '../../../utils/quest/optimisticQuestState';
import { getQuestsData } from '../questCatalog';
import { taskAdLog } from '../../../utils/quest/taskAdDebugLog';

/** TEMPORARY (see taskAdDebugLog.ts): this whole claim flow is shared by every
 * quest, but only quest 41 needs tracing right now — gate every log call on
 * that so quest 8 and everything else stays silent. */
const isQuest41 = (questId: number | undefined) => questId === 41;

async function claimQuestRewardViaSdk(userQuestId: number) {
  const { data } = await claimQuestRewardUserQuestsClaimRewardPost({
    body: { user_quest_id: userQuestId },
    throwOnError: true,
  });
  return data;
}

export interface UseQuestRewardClaimingParams {
  claimingQuestIds: Set<number>;
  setClaimedQuestIds: Dispatch<SetStateAction<Set<number>>>;
  optimisticallyClaimedRewardsRef: { current: Set<number> };
  setClaimingQuestIds: Dispatch<SetStateAction<Set<number>>>;
  questsWithoutSorting: DailyQuestView[];
  sortedQuests: DailyQuestView[];
  setSortedQuests: Dispatch<SetStateAction<DailyQuestView[]>>;
  t: TFunction;
  queryClient: QueryClient;
  playExpGift: () => void;
  playCurrencyGift: () => void;
}

export interface UseQuestRewardClaimingResult {
  handleClaimReward: (userQuestId: number) => Promise<void>;
}

export function useQuestRewardClaiming({
  claimingQuestIds,
  setClaimedQuestIds,
  optimisticallyClaimedRewardsRef,
  setClaimingQuestIds,
  questsWithoutSorting,
  setSortedQuests,
  t,
  queryClient,
  playExpGift,
  playCurrencyGift,
}: UseQuestRewardClaimingParams): UseQuestRewardClaimingResult {
  const claimsInFlightRef = useRef(new Set<number>());

  const handleClaimReward = async (userQuestId: number) => {
    // Looked up early, purely for the quest-41 diagnostic below — the real
    // lookup (computedQuest) still happens after the guard, unchanged.
    const questIdForLog = questsWithoutSorting.find((q) => q.userQuestId === userQuestId)?.questId;
    if (isQuest41(questIdForLog)) {
      taskAdLog('claimEntered', {
        userQuestId,
        alreadyInFlight: claimsInFlightRef.current.has(userQuestId),
        alreadyClaiming: claimingQuestIds.has(userQuestId),
        alreadyOptimisticallyClaimed: optimisticallyClaimedRewardsRef.current.has(userQuestId),
      });
    }

    // React state updates are asynchronous, so use a synchronous mutex as the
    // authoritative duplicate-click guard.
    if (
      claimsInFlightRef.current.has(userQuestId) ||
      claimingQuestIds.has(userQuestId) ||
      optimisticallyClaimedRewardsRef.current.has(userQuestId)
    ) {
      if (isQuest41(questIdForLog)) taskAdLog('claimRejected_guard', { userQuestId });
      return;
    }

    // Перевіряємо, чи квест завершений (використовуємо обчислені дані з questsWithoutSorting)
    // Шукаємо квест в обчислених даних, де isFinished вже правильно визначений
    const computedQuest = questsWithoutSorting.find((q) => q.userQuestId === userQuestId);
    if (!computedQuest || !computedQuest.isFinished) {
      if (isQuest41(questIdForLog)) {
        taskAdLog('claimRejected_notFinished', { userQuestId, found: !!computedQuest, isFinished: computedQuest?.isFinished ?? null });
      }
      showAlert(t("Quest is not completed yet. Complete all objectives first!"));
      return;
    }

    // Для отримання quest_id використовуємо дані з обчисленого квесту
    const questId = computedQuest.questId;
    // Отримуємо дані квесту з прямими значеннями нагороди
    const questsData = getQuestsData({ t });
    const questData = questsData.find((c) => c.questId === questId) || {
      questId,
      title: "",
      description: "",
      rewardAmount: 0,
      rewardType: "exp" as const,
    };
    const rewardAmount = questData.rewardAmount || 0;
    const isExpReward = questData.rewardType === "exp";
    // Catalog dual-reward fields (optional on QuestCatalogEntry; not on API Quest type).
    const secondaryRewardAmount = questData.secondaryRewardAmount || 0;
    const secondaryRewardType = questData.secondaryRewardType || "currency";

    if (computedQuest.isRewardClaimed) return;

    // No spinner state: the card flips to "claimed" synchronously below, so the
    // player never sees a pending step. This ref is only a network-level mutex.
    claimsInFlightRef.current.add(userQuestId);

    posthog.capture('quest_reward_claim_started', {
      quest_id: questId,
      user_quest_id: userQuestId,
      reward_type: questData.rewardType,
      reward_amount: rewardAmount,
      secondary_reward_type: secondaryRewardType,
      secondary_reward_amount: secondaryRewardAmount,
    });

    impactOccurred("light");

    const markClaimed = () => {
      if (isQuest41(questId)) taskAdLog('claimMarkedLocally', { userQuestId });
      setClaimedQuestIds(prev => new Set(prev).add(userQuestId));
      // Module-level record, so a slow /assign-and-get response that left the
      // server BEFORE this tap cannot put the button back to claimable.
      markRewardClaimedLocally(userQuestId);
      optimisticallyClaimedRewardsRef.current.add(userQuestId);
      globalClaimedQuestTimestamps.set(userQuestId, Date.now());
      setSortedQuests(prevSorted =>
        prevSorted.map(q =>
          q.userQuestId === userQuestId
            ? { ...q, isRewardClaimed: true }
            : q
        )
      );
    };

    const unmarkClaimed = () => {
      if (isQuest41(questId)) taskAdLog('claimUnmarked', { userQuestId });
      setClaimedQuestIds(prev => {
        if (!prev.has(userQuestId)) return prev;
        const newSet = new Set(prev);
        newSet.delete(userQuestId);
        return newSet;
      });
      unmarkRewardClaimedLocally(userQuestId);
      optimisticallyClaimedRewardsRef.current.delete(userQuestId);
      globalClaimedQuestTimestamps.delete(userQuestId);
      setSortedQuests(prevSorted =>
        prevSorted.map(q =>
          q.userQuestId === userQuestId
            ? { ...q, isRewardClaimed: false }
            : q
        )
      );
    };

    // Everything the player can perceive happens synchronously, before any
    // network call: the card flips to claimed, the sound plays and the balance
    // goes up. The request is sent afterwards and merely persists that.
    markClaimed();

    if (isExpReward) {
      playExpGift();
    } else {
      playCurrencyGift();
    }

    queryClient.setQueryData(["userData"], (old: UserData | undefined) => {
      if (!old) return old;
      const expToAdd = (isExpReward ? rewardAmount : 0) + (secondaryRewardType === "exp" ? secondaryRewardAmount : 0);
      const pearlsToAdd = (!isExpReward && questData.rewardType === "currency" ? rewardAmount : 0)
        + (secondaryRewardType === "currency" ? secondaryRewardAmount : 0);

      return applyUserProgressDelta({ ...old }, {
        expDelta: expToAdd > 0 ? expToAdd : undefined,
        pearlDelta: pearlsToAdd > 0 ? pearlsToAdd : undefined,
      });
    });

    posthog.capture('quest_completed', {
      quest_id: questId,
      user_quest_id: userQuestId,
      reward_type: questData.rewardType,
      reward_amount: rewardAmount,
    });

    // Fire-and-forget: the player is already free to keep playing.
    void (async () => {
      try {
        const response = await claimQuestRewardViaSdk(userQuestId);
        if (isQuest41(questId)) taskAdLog('claimNetworkOk', { userQuestId, userItemId: response.user_item_id });
        await activateUserItemUserInventoryUserItemIdActivatePost({
          path: { user_item_id: response.user_item_id },
          throwOnError: true,
        });
        if (isQuest41(questId)) taskAdLog('claimActivateOk', { userQuestId });
        // Replace the predicted balance with the authoritative one.
        void queryClient.invalidateQueries({ queryKey: ["userData"] });
      } catch (error: unknown) {
        const apiError = toApiError(error);
        const errorMessage = apiError.detail.toLowerCase();

        if (errorMessage.includes("already claimed")) {
          if (isQuest41(questId)) taskAdLog('claimNetworkAlreadyClaimed', { userQuestId });
          // A retry after a lost response. The reward exists on the server, so
          // the card stays claimed; only the numbers get reconciled.
          void queryClient.invalidateQueries({ queryKey: ["userData"] });
          return;
        }

        // A genuine failure. Undo the prediction quietly and let the player try
        // again, rather than interrupting them with an alert over a reward the
        // UI already showed as granted.
        if (isQuest41(questId)) {
          taskAdLog('claimNetworkFailed', { userQuestId, detail: apiError.detail.slice(0, 160) });
        }
        console.error("Failed to claim reward:", error);
        unmarkClaimed();
        void queryClient.invalidateQueries({ queryKey: ["userData"] });
      } finally {
        claimsInFlightRef.current.delete(userQuestId);
        setClaimingQuestIds(prev => {
          if (!prev.has(userQuestId)) return prev;
          const newSet = new Set(prev);
          newSet.delete(userQuestId);
          return newSet;
        });
      }
    })();
  };

  return {
    handleClaimReward,
  };
}
